import { menhirClient } from '@/common/integrations/menhir/menhir.client';
import {
  MENHIR_ENDPOINTS,
  endpointFor,
} from '@/common/integrations/menhir/menhir.endpoints';
import { IMenhirPaginationResponse } from '@/common/types/menhir/general';
import {
  transformInsuranceAvailablePlan,
  transformInsuranceCoveredMember,
  transformInsuranceDuplicatedFamilyMember,
  transformInsuranceNotCoveredMembers,
  transformInsurancePolicies,
  transformInsurancePolicy,
  transformInsurancePolicyOperator,
  transformInsuranceUpgradePlan,
  transformPayrollMovements,
  transformPlanEligibility,
  transformPlanSimulation,
} from './insurance.dto';
import {
  IMenhirInsuranceCoveredMember,
  IMenhirInsuranceNotCoveredMember,
  IMenhirInsurancePayrollMovement,
  IMenhirInsurancePolicies,
  IMenhirInsurancePolicy,
  InsurancePaymentMethod,
  TMenhirInsuranceAvailablePlan,
  TMenhirInsuranceDuplicatedMember,
  TMenhirInsurancePaymentMethod,
  TMenhirInsurancePlanEligibility,
  TMenhirInsurancePlanSimulation,
  TMenhirInsurancePolicyOperator,
  TMenhirInsuranceUpgradePlan,
  TMenhirPlanConfirmation,
  TMenhirPlanEligibility,
  TMenhirPlanSimulation,
  TPlanEstimations,
} from './insurance.types';

export const getInsurancePolicies = () =>
  menhirClient
    .get<
      null,
      IMenhirInsurancePolicies
    >(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT)
    .then(transformInsurancePolicies);

export const getInsurancePolicy = (policyId: string) =>
  menhirClient
    .get<
      null,
      { insurance: IMenhirInsurancePolicy }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.POLICY, policyId))
    .then((envelope) => transformInsurancePolicy(envelope.insurance));

export const getInsuranceCoveredMember = (policyId: string, memberId: string) =>
  menhirClient
    .get<
      null,
      { covered_person: IMenhirInsuranceCoveredMember }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.COVERED_MEMBER, policyId, memberId))
    .then((envelope) =>
      transformInsuranceCoveredMember(envelope.covered_person)
    );

export const updateInsuranceCoveredMemberPayment = (
  policyId: string,
  memberId: string,
  paymentMethod: InsurancePaymentMethod
) =>
  menhirClient.patch(
    endpointFor(
      MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.COVERED_MEMBER,
      policyId,
      memberId
    ),
    {
      payment_type: paymentMethod,
    }
  );

export const addInsuranceCoveredMember = ({
  policyId,
  memberId,
  paymentMethod,
  useGracePeriod,
}: {
  policyId: string;
  memberId: string;
  paymentMethod?: InsurancePaymentMethod;
  useGracePeriod?: boolean;
}) =>
  menhirClient.post(
    endpointFor(
      MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.COVERED_MEMBER,
      policyId,
      memberId
    ),
    {
      payment_type: paymentMethod,
      use_grace_period: useGracePeriod,
    }
  );

export const removeFromPolicy = ({ policyId }: { policyId: string }) =>
  menhirClient.post(
    endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.REMOVE_FROM_POLICY),
    {
      policy_id: policyId,
    }
  );

export const getInsuranceNotCoveredMembers = (policyId: string) =>
  menhirClient
    .get<
      null,
      { family_members: IMenhirInsuranceNotCoveredMember[] }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.NOT_COVERED_MEMBERS, policyId))
    .then((envelope) =>
      transformInsuranceNotCoveredMembers(envelope.family_members)
    );

export const getInsuranceDuplicatedFamilyMembers = () =>
  menhirClient
    .get<
      null,
      { duplicated_family_members: TMenhirInsuranceDuplicatedMember[] }
    >(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.DUPLICATED_FAMILY_MEMBERS)
    .then((envelope) =>
      envelope.duplicated_family_members.map(
        transformInsuranceDuplicatedFamilyMember
      )
    );

export const getUpgradeOptions = () =>
  menhirClient
    .get<
      undefined,
      { plans: TMenhirInsuranceUpgradePlan[] }
    >(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.UPGRADE_OPTIONS)
    .then((envelope) => {
      return envelope.plans.map(transformInsuranceUpgradePlan);
    });

export const upgradeInsurancePlan = ({
  policyId,
  paymentMethod,
  familyMemberIds,
}: {
  policyId: string;
  paymentMethod?: TMenhirInsurancePaymentMethod;
  familyMemberIds: string[];
}) =>
  menhirClient.post(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.UPGRADE_OPTIONS, {
    policy_id: policyId,
    payment_method: paymentMethod,
    family_member_ids: familyMemberIds,
  });

export const getInsurncePayrollMovements = (policyId: string) =>
  menhirClient
    .get<
      null,
      {
        payroll_payments: IMenhirPaginationResponse<IMenhirInsurancePayrollMovement>;
      }
    >(
      endpointFor(
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PAYROLL_PAYMENTS,
        policyId
      ),
      { params: { pagination: 'no' } }
    )
    .then((envelope) =>
      transformPayrollMovements(envelope.payroll_payments.list)
    );

export const getAvailableOptions = () =>
  menhirClient
    .get<
      undefined,
      { plans: TMenhirInsuranceUpgradePlan[] }
    >(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.AVAILABLE_OPTIONS)
    .then((envelope) => {
      return envelope.plans.map(transformInsuranceAvailablePlan);
    });

export const getSelectedPlanEstimations = ({
  policyId,
  personsIds,
}: TPlanEstimations) =>
  menhirClient
    .post<
      undefined,
      { estimation: TMenhirInsuranceAvailablePlan }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PLAN_ESTIMATIONS), { policy_id: policyId, family_members_ids: personsIds })
    .then((envelope) => {
      return transformInsuranceAvailablePlan(envelope.estimation);
    });

export const confirmHealthInsurancePlan = ({
  policyId,
  familyMemberIds,
}: {
  policyId: string;
  familyMemberIds: string[];
}) =>
  menhirClient.post(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.CONFIRM_OPT_IN, {
    policy_id: policyId,
    family_member_ids: familyMemberIds,
  });

export const getOptInEligibility = () =>
  menhirClient
    .get<
      null,
      { eligible: boolean }
    >(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.OPT_IN_ELIGIBILTY)
    .then((envelope) => {
      return envelope.eligible;
    });

export const createPlanSimulation = ({
  policyId,
  persons,
}: TMenhirPlanSimulation) => {
  return menhirClient
    .post<
      undefined,
      { simulation: TMenhirInsurancePlanSimulation }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PLAN_SIMULATION), { policy_id: policyId, persons })
    .then((envelope) => transformPlanSimulation(envelope.simulation));
};

export const createPlanEligibility = ({
  policyId,
  persons,
}: TMenhirPlanEligibility) => {
  return menhirClient
    .post<
      undefined,
      { eligibility: TMenhirInsurancePlanEligibility }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PLAN_ELIGIBILITY), { policy_id: policyId, persons })
    .then((envelope) => {
      return transformPlanEligibility(envelope.eligibility);
    });
};

export const createPlanConfirmation = (payload: TMenhirPlanConfirmation) => {
  return menhirClient.post(
    endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PLAN_CONFIRMATION),
    payload
  );
};

export const getInsurancePolicyOperator = (policyId: string) =>
  menhirClient
    .get<
      null,
      { operator: TMenhirInsurancePolicyOperator }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.POLICY_OPERATOR, policyId))
    .then((envelope) => transformInsurancePolicyOperator(envelope.operator));
